import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";

import { ApiError, staffLinkSignIn } from "@/api/client";
import { AuthFrame } from "@/auth/AuthFrame";
import { useAuth } from "@/auth/AuthProvider";
import { STAFF_HOME } from "@/auth/next";
import { Button } from "@/ui/Button";
import { Notice } from "@/ui/Notice";
import { Spinner } from "@/ui/primitives";

const back = (
  <Link to="/sign-in" className="text-muted hover:text-ink">
    Back to sign in
  </Link>
);

/**
 * Where a break-glass staff link lands, for when Google is unavailable (ADR-0016). The
 * token is in the fragment, so it never reaches a server log, and it is spent only on a
 * click: a link preview that opens the page does not sign anyone in.
 */
export function StaffLinkScreen() {
  const token = useLocation().hash.replace(/^#/, "");
  const navigate = useNavigate();
  const { adopt } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const signIn = async () => {
    setBusy(true);
    setError(null);
    try {
      adopt(await staffLinkSignIn(token));
      void navigate(STAFF_HOME, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "The server could not be reached.");
      setBusy(false);
    }
  };

  return (
    <AuthFrame footer={back}>
      <h1 className="text-lg font-semibold text-ink">Darkstar staff sign-in link</h1>
      {!token ? (
        <Notice tone="danger" className="mt-4">
          This link is incomplete. Open it again exactly as you were given it.
        </Notice>
      ) : (
        <>
          <p className="mt-1 text-sm text-muted">
            For when Google sign-in is unavailable. The link works once.
          </p>
          {error ? (
            <Notice tone="danger" className="mt-4">
              {error}
            </Notice>
          ) : null}
          <Button
            variant="primary"
            className="mt-5 w-full"
            disabled={busy}
            onClick={() => void signIn()}
          >
            {busy ? <Spinner className="size-4" /> : null}
            Sign in to the platform console
          </Button>
        </>
      )}
    </AuthFrame>
  );
}
